import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { FastField, Field, Form, Formik } from 'formik';
import _ from 'lodash';

import notification from 'app/config/notification';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { errorHandler } from 'app/utils/errorHandler';

import {
    clearDuplicatedRegistry,
    clearLineLocations,
    clearLineSections,
    clearSituations,
    fetchItineraryTypes,
    fetchLine,
    fetchLocations,
    fetchSections,
    fetchSituations,
} from 'app/store/slices/line';

import { makeStyles } from '@material-ui/core';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';

import Loading from 'app/layouts/Loading';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import DefaultTable from '../components/DefaultTable';
import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import AddButton from '../components/AddButton';
import ItineraryLocalModal from './modals/ItineraryLocalModal';
import ItinerarySectionModal from './modals/ItinerarySectionModal';
import DialogItineraryLocal from './modals/DialogItineraryLocal';
import { itineraryInitialValues } from '../form-configs/itinerary-initial-values';
import { itinerariesValidationSchema } from '../form-configs/validation-schemas';
import { ITINERARY_TYPES, LINES_SITUATIONS } from 'app/config/services';

import styles from './styles';
import { generatePath } from 'react-router-dom';
import { URLS } from '../../../config/urls';
import { validateFormikForm } from '../../../utils/forms/functions';
import InfoLineCompany from '../components/InfoLineCompany';
import { OfCheckBox } from '../components/CellType';

const ItinerariesDetails = (props) => {
    const history = useHistory();
    const match = useRouteMatch();
    const classes = useStyles();
    const dispatch = useDispatch();

    const cdLinhaFromPath = React.useRef(match.params.cdLinha).current;
    const cdItinerarioFromPath = React.useRef(match.params.cdItinerario).current;

    const [loading, setLoading] = useState(true);

    const [totalDistance, setTotalDistance] = useState(0);
    const [openLocal, setOpenLocal] = useState(false);
    const [openSection, setOpenSection] = useState(false);

    const [selectedItinerary, setSelectedItinerary] = useState(null);
    const [formInitials, setFormInitials] = useState(itineraryInitialValues);
    const [rows, setRows] = useState([]);
    const [openDialogLocal, setOpenDialogLocal] = useState(false);
    const [localidades, setLocalidades] = useState([]);
    const [savingLocationOnList, setSavingLocationOnList] = useState(false);
    const [itineraryType, setItineraryType] = useState(0);

    const [columns, setColumns] = useState(() => [
        { id: 0, fieldName: 'nuSecao', title: 'Seção' },
        { id: 1, fieldName: 'deLocalidade', title: 'Localidade' },
        { id: 2, fieldName: 'nuPisoUm', title: 'Piso I' },
        { id: 3, fieldName: 'nuPisoDois', title: 'Piso II' },
        { id: 4, fieldName: '', title: '' },
    ]);

    const situationsStore = useSelector((state) => state.line.situations);
    const itineraryTypesStore = useSelector((state) => state.line.itineraryTypes);
    const selectedLineStore = useSelector((state) => state.line.selected);
    const locationsStore = useSelector((state) => state.line.lineLocations);
    const sectionsStore = useSelector((state) => state.line.lineSections);
    const duplicatedItinerary = useSelector((state) => state.line.duplicatedRegistry);
    const isStrip = useSelector((state) => state.line.isStrip);

    const canEdit = useMemo(() => {
        return !(
            selectedItinerary &&
            selectedItinerary.cdLinhaSituacao &&
            selectedItinerary.cdLinhaSituacao !== LINES_SITUATIONS.EM_REVISAO.code
        );
    }, [selectedItinerary]);

    const showNotification = (msg, type) => {
        notification(msg, {
            toastId: 'VALIDATION',
            type,
        });
    };

    useEffect(() => {
        setLoading(true);
        (async () => {
            const promisses = [];
            if (situationsStore.length === 0) promisses.push(dispatch(fetchSituations()));
            if (locationsStore.length === 0) promisses.push(dispatch(fetchLocations()));
            if (sectionsStore.length === 0) promisses.push(dispatch(fetchSections()));
            if (itineraryTypesStore.length === 0) promisses.push(dispatch(fetchItineraryTypes()));
            if (!selectedLineStore && cdLinhaFromPath) promisses.push(dispatch(fetchLine(cdLinhaFromPath)));
            if (promisses.length > 0) await Promise.all(promisses);
            setLoading(false);
        })();
        return () => {
            dispatch(clearSituations());
            dispatch(clearLineLocations());
            dispatch(clearLineSections());
            dispatch(clearDuplicatedRegistry());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (() => {
            if (isStrip) {
                setColumns([
                    { id: 0, fieldName: 'nuSecao', title: 'Seção' },
                    { id: 1, fieldName: 'deLocalidade', title: 'Localidade' },
                    { id: 1, fieldName: 'flAcesso', title: 'Acesso', Component: OfCheckBox },
                    { id: 2, fieldName: 'nuPisoUm', title: 'Piso I' },
                    { id: 3, fieldName: 'nuPisoDois', title: 'Piso II' },
                    { id: 4, fieldName: '', title: '' },
                ]);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStrip]);

    useEffect(() => {
        (() => {
            if (duplicatedItinerary && Object.keys(duplicatedItinerary).length > 0) {
                setLoading(true);
                const itinerary = _.cloneDeep(duplicatedItinerary);
                itinerary.cdRevisao = selectedLineStore?.qtdItinerarios ? selectedLineStore.qtdItinerarios + 1 : '1';
                itinerary.cdLinhaSituacao = LINES_SITUATIONS.EM_REVISAO.code;
                itinerary.cdLinhaItinerario = null;
                itinerary.nuAditivo = '';
                itinerary.dtCadastro = new Date();
                setSelectedItinerary(itinerary);
                setItineraryType(itinerary.cdTipoItinerario || 0);
                setFormInitials({ ...itineraryInitialValues, ...itinerary });
                if (itinerary.localidades) {
                    const tempLocalidades = itinerary.localidades.map((local) => {
                        const temp = { ...local };
                        delete temp.cdItinerarioLocalidade;
                        delete temp.cdLinhaItinerario;
                        temp.checked = temp.flAcesso;
                        return temp;
                    });
                    itinerary.localidades = tempLocalidades;
                    setLocalidades(tempLocalidades);
                }
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duplicatedItinerary, selectedLineStore]);

    useEffect(() => {
        (async () => {
            try {
                if (cdItinerarioFromPath) {
                    setLoading(true);
                    if (!selectedItinerary) {
                        const response = await lineEndpoint.getItinerary(cdItinerarioFromPath);
                        if (response && response.status === 200) {
                            const itinerary = response.data;
                            setSelectedItinerary(itinerary);
                            setFormInitials({ ...itineraryInitialValues, ...itinerary });
                            setItineraryType(itinerary.cdTipoItinerario || 0);
                            if (itinerary.localidades) {
                                const tempLocalidades = itinerary.localidades.map((local) => {
                                    const temp = { ...local };
                                    temp.checked = temp.flAcesso;
                                    temp.lockCheckbox = itinerary.cdLinhaSituacao !== LINES_SITUATIONS.EM_REVISAO.code;
                                    return temp;
                                });
                                setLocalidades(tempLocalidades);
                            }
                        }
                    }
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdItinerarioFromPath]);

    const getSectionFromLocations = (cdInitialLocation, cdFinalLocation) => {
        return sectionsStore.find((section) => {
            return (
                section.cdLocalidadeInicial === Number(cdInitialLocation) &&
                section.cdLocalidadeFinal === Number(cdFinalLocation)
            );
        });
    };

    const prepareLocations = (locationsParam = []) => {
        let cdLocalidadeAnterior = 0;
        let cdPrimeiraLocalidade = 0;
        const tempRows = [];
        let totalKm = 0;
        const lastElementIndex = locationsParam.length - 1;
        let count = 0;
        for (const location of locationsParam) {
            const temp = { ...location };
            if (count === 0) cdPrimeiraLocalidade = temp.cdLinhaLocalidade;
            temp.deLocalidade = location.localidade?.deNome;
            if (Number(itineraryType) === ITINERARY_TYPES.DISTANCIA_ENTRE_SECOES && cdLocalidadeAnterior !== 0) {
                if (!temp.secao) {
                    const responseSection = getSectionFromLocations(cdLocalidadeAnterior, temp.cdLinhaLocalidade);
                    if (responseSection) {
                        temp.secao = responseSection;
                    }
                }
                temp.nuPisoUm = temp.secao?.nuPisoUm;
                temp.nuPisoDois = temp.secao?.nuPisoDois;
            } else {
                if (count === lastElementIndex) {
                    //last line
                    if (!temp.secao) {
                        const responseSection = getSectionFromLocations(cdPrimeiraLocalidade, temp.cdLinhaLocalidade);
                        if (responseSection) {
                            temp.secao = responseSection;
                        }
                    }
                    temp.nuPisoUm = temp.secao?.nuPisoUm;
                    temp.nuPisoDois = temp.secao?.nuPisoDois;
                }
            }
            cdLocalidadeAnterior = temp.cdLinhaLocalidade;
            if (temp.nuPisoUm && temp.nuPisoUm !== '') {
                totalKm += Number(temp.nuPisoUm);
            }
            if (temp.nuPisoDois && temp.nuPisoDois !== '') {
                totalKm += Number(temp.nuPisoDois);
            }
            let backgroundColor = 'transparent';
            if (!temp.cdItinerarioLocalidade) backgroundColor = '#b8f5b8';
            if (temp.boExcluido) backgroundColor = '#fbcfca';
            temp.style = { backgroundColor };
            tempRows.push(temp);
            count++;
        }
        setTotalDistance(totalKm);
        return tempRows;
    };

    useEffect(() => {
        (async () => {
            setRows(prepareLocations(localidades));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localidades, sectionsStore, itineraryType]);

    const handleCloseModal = (tipo) => {
        if (tipo === 'local') setOpenLocal(false);
        else if (tipo === 'section') setOpenSection(false);
    };

    const handleOpenModal = (tipo) => {
        if (tipo === 'local') setOpenLocal(true);
        else if (tipo === 'section') setOpenSection(true);
    };

    const handleDeleteLocation = (entry, data) => {
        const localidadesChanged = [];
        let index = 0;
        for (const local of localidades) {
            const temp = { ...local };
            if (Number(local.nuSecao) === Number(data.nuSecao)) {
                if (temp.boExcluido) {
                    delete temp.boExcluido;
                } else {
                    if (index === 0) {
                        temp.boExcluido = true;
                    } else {
                        const previousLocation = localidades[index - 1];
                        const nextLocation = localidades[index + 1];
                        if (previousLocation) {
                            if (!nextLocation) {
                                temp.boExcluido = true;
                            } else {
                                if (nextLocation.boExcluido) {
                                    temp.boExcluido = true;
                                } else {
                                    const responseSection = getSectionFromLocations(
                                        previousLocation.cdLinhaLocalidade,
                                        nextLocation.cdLinhaLocalidade,
                                    );
                                    if (!responseSection) {
                                        showNotification(
                                            `Não foi possível excluir essa localização porque não existe seção cadastrada para as localidades ${previousLocation.localidade?.deNome} e ${nextLocation.localidade?.deNome}. Favor realizar o cadastro da seção`,
                                            'error',
                                        );
                                    } else {
                                        temp.boExcluido = true;
                                    }
                                }
                            }
                        }
                    }
                }
            }
            localidadesChanged.push(temp);
            index++;
        }
        setLocalidades(localidadesChanged);
    };

    const handleSaveLocalDialog = (value) => {
        setSavingLocationOnList(true);
        if (value?.cdLinhaLocalidade) {
            const newLocation = locationsStore.find((l) => l.cdLinhaLocalidade === Number(value.cdLinhaLocalidade));
            const locals = [...localidades];
            const localsNotDeleted = locals.filter((local) => !local.boExcluido);

            const newItineraryLocation = {
                cdLinhaLocalidade: newLocation.cdLinhaLocalidade,
                flAcesso: false,
                localidade: newLocation,
                nuSecao: locals.length + 1,
            };
            if (localsNotDeleted.length > 0) {
                const last = localsNotDeleted[localsNotDeleted.length - 1];
                const responseSection = getSectionFromLocations(last.cdLinhaLocalidade, newLocation.cdLinhaLocalidade);
                if (last && !responseSection) {
                    showNotification(
                        `Não encontrado as informações de Piso I e Piso II para ${last.localidade.deNome} e ${newLocation.deNome}. Favor realizar o cadastro da seção`,
                        'error',
                    );
                    setSavingLocationOnList(false);
                    return;
                } else {
                    newItineraryLocation.secao = responseSection;
                    newItineraryLocation.cdlinhaSecao = responseSection.cdLinhaSecao;
                }
            }
            if (cdItinerarioFromPath) {
                newItineraryLocation.cdLinhaItinerario = Number(cdItinerarioFromPath);
            }
            locals.push(newItineraryLocation);
            setLocalidades(locals);
            setOpenDialogLocal(false);
            setSavingLocationOnList(false);
        }
    };

    const handleChangeItiteraryType = (e) => {
        setItineraryType(e.target.value);
    };

    const updateItinerary = (data) => {
        setSelectedItinerary(data);
        setFormInitials({ ...itineraryInitialValues, ...data });
        if (data.localidades) {
            const tempLocalidades = data.localidades.map((local) => {
                const temp = { ...local };
                temp.checked = temp.flAcesso;
                return temp;
            });
            setLocalidades(tempLocalidades);
        }
    };

    const handleSubmitForm = async (values) => {
        try {
            let noLocations = !localidades || localidades.length === 0;
            if (!noLocations) {
                noLocations =
                    localidades.filter((l) => {
                        return !l.boExcluido;
                    }).length === 0;
            }

            if (noLocations) {
                showNotification(
                    'É necessário a inclusão de pelo menos uma localidade na lista de itinerário',
                    'error',
                );
                return;
            }

            setLoading(true);
            const valuesCopy = _.cloneDeep(values);
            valuesCopy.cdLinha = selectedLineStore.cdLinha;
            valuesCopy.localidades = localidades;

            const localsNotDeleted = localidades.filter((local) => !local.boExcluido);

            if (Number(itineraryType) === ITINERARY_TYPES.DISTACIA_CHEIA) {
                const cdPrimeiraLocalidade = localsNotDeleted[0].cdLinhaLocalidade;
                const cdUltimaLocalidade = localsNotDeleted[localsNotDeleted.length - 1].cdLinhaLocalidade;
                if (cdPrimeiraLocalidade && cdUltimaLocalidade && cdPrimeiraLocalidade !== cdUltimaLocalidade) {
                    const responseSection = getSectionFromLocations(cdPrimeiraLocalidade, cdUltimaLocalidade);
                    if (!responseSection) {
                        showNotification(
                            'Erro ao enviar. Não existe seção cadastrada para a primeira e última localidades.',
                            'error',
                        );
                        return;
                    }
                }
            }

            if (
                Number(valuesCopy.cdLinhaSituacao) === LINES_SITUATIONS.VIGENTE.code ||
                Number(valuesCopy.cdLinhaSituacao) === LINES_SITUATIONS.INATIVO.code
            ) {
                const keySituation = Object.keys(LINES_SITUATIONS).filter(
                    (key) => LINES_SITUATIONS[key].code === Number(valuesCopy.cdLinhaSituacao),
                );
                if (
                    !window.confirm(
                        `Deseja alterar a situação para ${LINES_SITUATIONS[keySituation].name}? Após essa mudança, não será permitido alterar os dados dessa revisão.`,
                    )
                ) {
                    return;
                }
            }

            const savedItinerary = await lineEndpoint.saveItinerary(valuesCopy);
            if (savedItinerary.status === 200) {
                showNotification('Revisão salva com sucesso', 'success');
                updateItinerary(savedItinerary.data);
                if (!cdItinerarioFromPath) {
                    let params = {
                        cdLinha: cdLinhaFromPath,
                        cdItinerario: savedItinerary.data.cdLinhaItinerario,
                    };
                    if (isStrip) params.isStrip = 'strip';
                    const url = generatePath(URLS.LINES_MANAGE_ITINERARIES_EDIT, params);
                    history.push(url);
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectItineraryAccess = ({ data, index }) => {
        let count = 0;
        const tempRows = localidades.map((row) => {
            const temp = { ...row };
            if (index === count && temp.cdLinhaLocalidade === data.cdLinhaLocalidade) {
                let checked = !temp.checked;
                temp.flAcesso = checked;
                temp.checked = checked;
            }
            count++;
            return temp;
        });
        setLocalidades(tempRows);
    };

    const handleCancel = () => {
        let tempLink = URLS.LINES_MANAGE_ITINERARIES;
        if (cdLinhaFromPath) {
            let params = {
                cdLinha: Number(cdLinhaFromPath),
            };
            if (isStrip) params.isStrip = 'strip';
            history.push(generatePath(tempLink, params));
        }
    };

    return (
        <>
            <DialogItineraryLocal
                {...props}
                open={openDialogLocal}
                handleCancel={() => setOpenDialogLocal(false)}
                handleSave={handleSaveLocalDialog}
                loading={savingLocationOnList}
            />
            <Formik
                enableReinitialize
                initialValues={formInitials}
                validationSchema={itinerariesValidationSchema}
                onSubmit={handleSubmitForm}
                validateOnBlur={false}
                validateOnChange={false}>
                {(formProps) => {
                    const { handleSubmit, handleChange, values } = formProps;

                    return (
                        <Form>
                            <PageWrapper
                                title="Cadastro / Edição de Itinerário"
                                actionButton={
                                    <div>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            className={classes.addButton}
                                            style={{ marginRight: '10px' }}
                                            onClick={() => handleOpenModal('local')}>
                                            Localidade
                                        </Button>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            className={classes.addButton}
                                            onClick={() => handleOpenModal('section')}>
                                            Itinerário - Seções
                                        </Button>
                                    </div>
                                }>
                                {loading && <Loading />}
                                <Grid container spacing={3}>
                                    <Grid item container spacing={3} direction={'row'}>
                                        <InfoLineCompany selectedLine={selectedLineStore} />
                                    </Grid>

                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={3}>
                                            <Field
                                                component={CustomTextField}
                                                label="Código da revisão"
                                                name="cdRevisao"
                                                fullWidth
                                                disabled
                                                loading={loading}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Field
                                                component={CustomTextField}
                                                label="Situação"
                                                name="cdLinhaSituacao"
                                                select
                                                loading={loading}
                                                disabled={loading || !canEdit}
                                                SelectProps={{
                                                    native: true,
                                                }}>
                                                <option value="">Selecione...</option>
                                                {situationsStore?.map((type, index) => (
                                                    <option key={`type-${index}`} value={type.cdLinhaSituacao}>
                                                        {type.deLinhaSituacao}
                                                    </option>
                                                ))}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={3}>
                                            <FastField
                                                component={CustomDateField}
                                                label="Data de cadastro"
                                                name="dtCadastro"
                                                disabled={!canEdit}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={5}>
                                            <Field
                                                component={CustomTextField}
                                                label="Tipo de itinerário"
                                                name="cdTipoItinerario"
                                                select
                                                loading={loading}
                                                disabled={loading || !canEdit}
                                                SelectProps={{
                                                    native: true,
                                                }}
                                                onChange={(e) => {
                                                    handleChange(e);
                                                    handleChangeItiteraryType(e);
                                                }}>
                                                <option value="">Selecione...</option>
                                                {itineraryTypesStore?.map((type, index) => (
                                                    <option key={`type-${index}`} value={type.cdTipoItinerario}>
                                                        {type.deTipoItinerario}
                                                    </option>
                                                ))}
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <div style={{ margin: '10px 0 -20px 10px' }}>
                                            Extensão total: {totalDistance} km
                                        </div>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DefaultTable
                                            columns={columns}
                                            rows={rows}
                                            onDelete={handleDeleteLocation}
                                            disabled={loading || !canEdit}
                                            entryProps={{
                                                onChangeSelect: handleSelectItineraryAccess,
                                                container: 'secoes',
                                            }}
                                            actionButton={
                                                <>
                                                    {loading || !canEdit ? (
                                                        <></>
                                                    ) : (
                                                        <AddButton
                                                            color="primary"
                                                            className={classes.addButton}
                                                            onClick={() => setOpenDialogLocal(true)}>
                                                            Adicionar
                                                        </AddButton>
                                                    )}
                                                </>
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FastField
                                            component={CustomTextField}
                                            name="deObservacao"
                                            label="Observação"
                                            multiline
                                            minRows={4}
                                            disabled={!canEdit}
                                            inputProps={{
                                                maxLength: 2000,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        onCancel={handleCancel}
                                        onSave={() => {
                                            validateFormikForm(formProps, itinerariesValidationSchema).then((res) => {
                                                if (Object.keys(res).length > 0) {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                } else {
                                                    handleSubmit(values);
                                                }
                                            });
                                        }}
                                        firstButtonLabel={'VOLTAR'}
                                        middleButtonLabel={canEdit ? 'SALVAR' : null}
                                    />
                                </Grid>
                            </PageWrapper>
                        </Form>
                    );
                }}
            </Formik>

            <ItineraryLocalModal
                open={openLocal}
                onClose={() => handleCloseModal('local')}
                showNotification={showNotification}
            />
            <ItinerarySectionModal
                open={openSection}
                onClose={() => handleCloseModal('section')}
                showNotification={showNotification}
            />
        </>
    );
};

const useStyles = makeStyles(styles);

export default ItinerariesDetails;
